import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import './xeroIntegration.css';
import { connectXero } from '../../../../utils/xeroConnection';
import { xeroNeedsReconnect, xeroHealthWarning, xeroRateLimited, xeroSyncNotice } from '../../../../utils/xeroStatus';
import { doc } from 'firebase/firestore';
import { safeOnSnapshot } from '../../../../utils/firestoreSubscriptions';
import { db } from '../../../../firebase-client';
import { useAuth } from '../../../../auth/AuthContext';
import {
  DISCONNECT_XERO_URL,
  REFRESH_XERO_METADATA_URL,
  RUN_XERO_BACKFILL_URL,
  RUN_XERO_SYNC_NOW_URL,
  SAVE_XERO_CONFIG_URL,
} from '../../../../utils/functionURLs';

const DEFAULT_FORM = {
  tenantId: '',
};

function buildFormFromConfig(config = null) {
  return {
    tenantId: config?.tenantId || '',
  };
}

function asDate(value) {
  if (value?.toDate) return value.toDate();
  if (value instanceof Date) return value;
  return null;
}

function accountLabel(account) {
  const code = String(account?.code || '').trim();
  const name = String(account?.name || '').trim();
  const type = String(account?.type || '').trim();
  return [code, name, type ? `(${type})` : ''].filter(Boolean).join(' ');
}

async function postWithAuth({ user, url, body }) {
  const token = await user.getIdToken();
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data?.error || 'Request failed');
  }
  return data;
}

export default function XeroIntegrationModal({ isOpen, onClose, teamId }) {
  const { user } = useAuth();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyAction, setBusyAction] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [syncResult, setSyncResult] = useState(null);
  const [form, setForm] = useState(DEFAULT_FORM);
  const isDirtyRef = useRef(false);

  const updateForm = (updater) => {
    isDirtyRef.current = true;
    setForm((prev) => (typeof updater === 'function' ? updater(prev) : updater));
  };

  useEffect(() => {
    if (!isOpen) {
      isDirtyRef.current = false;
      setForm(DEFAULT_FORM);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !teamId) return undefined;
    setLoading(true);
    setError('');
    setNotice('');
    setSyncResult(null);
    isDirtyRef.current = false;

    const unsub = safeOnSnapshot(
      doc(db, 'teams', teamId, 'integrations', 'xero'),
      (snap) => {
        const nextConfig = snap.exists() ? (snap.data() || {}) : null;
        setConfig(nextConfig);
        if (!isDirtyRef.current) {
          setForm(buildFormFromConfig(nextConfig));
        }
        setLoading(false);
      },
      (snapshotError) => {
        setError(snapshotError?.message || 'Unable to load Xero config.');
        setLoading(false);
      }
    );

    return () => unsub();
  }, [isOpen, teamId]);

  const tenantOptions = useMemo(
    () => {
      const tenants = Array.isArray(config?.availableTenants) ? config.availableTenants : [];
      return config?.tenantId && !tenants.some(tenant => tenant.tenantId === config.tenantId)
        ? [{ tenantId: config.tenantId, tenantName: config.tenantName || 'Current organisation' }, ...tenants]
        : tenants;
    },
    [config]
  );
  const accountOptions = useMemo(
    () => Array.isArray(config?.availableAccounts) ? config.availableAccounts : [],
    [config]
  );
  const managedClearingCode = config?.managedStripeClearingAccountCode || config?.mappings?.stripeClearingAccountCode || '';
  const managedClearingAccount = accountOptions.find((account) => (
    account.code === managedClearingCode || account.accountId === config?.managedStripeClearingAccountId
  )) || null;
  const backfillStatus = config?.backfillStatus || null;
  const lastSyncAt = asDate(config?.lastSyncAt);
  const latestErrorAt = asDate(config?.lastSyncErrorAt);
  const lastConnectionCheckAt = asDate(config?.lastConnectionCheckAt);
  const needsReconnect = xeroNeedsReconnect(config);
  const healthWarning = xeroHealthWarning(config);
  const rateLimited = xeroRateLimited(config) && !needsReconnect && !config?.queueSummary?.failed;

  const needsSetup = config?.connected && (!config.tenantId || config.connectionStatus === 'selection_required' || config.syncMode === 'disabled');
  const organisationChanged = form.tenantId !== (config?.tenantId || '');
  const status = needsReconnect ? 'Reconnection required' : rateLimited ? 'Waiting for Xero' : needsSetup ? 'Finish setup' : healthWarning ? 'Needs attention' : config?.connected ? 'Connected' : 'Not connected';
  const historyState = ({ idle: 'No history import yet', running: 'Import in progress', queued: 'Imported and queued for Xero', completed: 'History synced', failed: 'Import needs attention' })[backfillStatus?.state] || '';

  if (!isOpen) return null;

  const handleConnect = async () => {
    if (!user) return;
    setBusyAction('connect');
    setError('');
    try {
      await connectXero({ user, teamId });
    } catch (err) {
      setError(err?.message || 'Unable to connect Xero.');
    } finally {
      setBusyAction('');
    }
  };

  const handleDisconnect = async () => {
    if (!user) return;
    setBusyAction('disconnect');
    setError('');
    try {
      await postWithAuth({
        user,
        url: DISCONNECT_XERO_URL,
        body: { teamId },
      });
    } catch (err) {
      setError(err?.message || 'Unable to disconnect Xero.');
    } finally {
      setBusyAction('');
    }
  };

  const handleRefreshMetadata = async () => {
    if (!user) return;
    setBusyAction('refresh');
    setError('');
    setNotice('');
    setSyncResult(null);
    try {
      await postWithAuth({
        user,
        url: REFRESH_XERO_METADATA_URL,
        body: { teamId, tenantId: form.tenantId },
      });
      setNotice('Xero account details refreshed.');
    } catch (err) {
      setError(err?.message || 'Unable to refresh Xero accounts.');
    } finally {
      setBusyAction('');
    }
  };

  const handleSave = async () => {
    if (!user) return;
    setBusyAction('save');
    setError('');
    setNotice('');
    setSyncResult(null);
    try {
      const tenantName = tenantOptions.find((tenant) => tenant.tenantId === form.tenantId)?.tenantName || '';
      await postWithAuth({
        user,
        url: SAVE_XERO_CONFIG_URL,
        body: {
          teamId,
          tenantId: form.tenantId,
          tenantName,
        },
      });
      isDirtyRef.current = false;
      setNotice('Xero settings saved. Automatic syncing is enabled.');
    } catch (err) {
      setError(err?.message || 'Unable to save Xero settings.');
    } finally {
      setBusyAction('');
    }
  };

  const handleBackfill = async () => {
    if (!user) return;
    setBusyAction('backfill');
    setError('');
    setSyncResult(null);
    setNotice('');
    try {
      await postWithAuth({
        user,
        url: RUN_XERO_BACKFILL_URL,
        body: { teamId },
      });
      setNotice('History has been imported and queued for Xero. It will continue automatically; use Sync now to start a pass straight away.');
    } catch (err) {
      setError(err?.message || 'Unable to run Xero backfill.');
    } finally {
      setBusyAction('');
    }
  };

  const handleSyncNow = async () => {
    if (!user) return;
    setBusyAction('sync_now');
    setError('');
    try {
      const result = await postWithAuth({
        user,
        url: RUN_XERO_SYNC_NOW_URL,
        body: { teamId },
      });
      setNotice('');
      setSyncResult(result);
      if (result.ok === false) throw new Error(`${result.failed || 'Some'} sync jobs failed. Check the latest error and retry after resolving it.`);
    } catch (err) {
      setError(err?.message || 'Unable to run Xero sync now.');
    } finally {
      setBusyAction('');
    }
  };

  const organisationPicker = (
    <label className="xero-field">
      <span>Xero organisation</span>
      <select className="input" value={form.tenantId} disabled={Boolean(busyAction) || needsReconnect}
        onChange={event => updateForm(prev => ({ ...prev, tenantId: event.target.value }))}>
        <option value="">Select a Xero organisation</option>
        {tenantOptions.map(tenant => <option key={tenant.tenantId || tenant.id} value={tenant.tenantId}>{tenant.tenantName || 'Xero organisation'}</option>)}
      </select>
    </label>
  );

  return ReactDOM.createPortal(
    <div className="modal-backdrop">
      <div className="modal-content xero-integration" role="dialog" aria-modal="true" aria-label="Xero integration settings">
        <div className="card-header-with-icon xero-integration__header">
          <h2 className="modal-title">Xero integration</h2>
          <button type="button" className="edit-btn" onClick={onClose} aria-label="Close Xero settings"><span aria-hidden="true">×</span></button>
        </div>
        {loading ? <p className="text-muted">Loading Xero settings…</p> : (
          <div className="xero-integration__body">
            <p className="xero-integration__intro">Keep your club’s Stripe payments, refunds and payouts together in Xero.</p>
            {error ? <p className="text-error" role="alert">{error}</p> : null}
            {notice ? <p className="xero-feedback" role="status">{notice}</p> : null}
            {syncResult ? <p className="xero-feedback" role="status">{xeroSyncNotice({
              ...syncResult,
              remaining: config?.queueSummary?.pending ?? syncResult.remaining,
              failedJobs: config?.queueSummary?.failed ?? syncResult.failedJobs,
              deferred: config?.syncPause?.reason === 'rate_limit' ? 1 : config?.syncPause === null ? 0 : syncResult.deferred,
            })}</p> : null}

            <section className="xero-panel" aria-label="Connection and sync status">
              <div className="xero-panel__heading">
                <div><span className="xero-eyebrow">Xero connection</span><h3>{config?.connected ? config.tenantName || 'Choose your organisation' : 'Connect your club'}</h3></div>
                <span className={`xero-status ${needsReconnect || (healthWarning && !rateLimited && !needsSetup) ? 'xero-status--attention' : rateLimited || needsSetup ? 'xero-status--waiting' : config?.connected ? 'xero-status--connected' : ''}`}>{status}</span>
              </div>
              {!config?.connected ? (
                <>
                  <p className="text-muted">Sign in to Xero and authorise the organisation you use for this club. You’ll return here to finish setup.</p>
                  <div className="xero-actions"><button type="button" className="btn btn--primary" onClick={handleConnect} disabled={Boolean(busyAction)}>{busyAction === 'connect' ? 'Connecting…' : 'Connect Xero'}</button></div>
                </>
              ) : (
                <>
                  <dl className="xero-metrics" aria-label="Sync queue">
                    <div><dt>Queued records</dt><dd>{config.queueSummary?.pending || 0}</dd></div>
                    <div><dt>Failed records</dt><dd>{config.queueSummary?.failed || 0}</dd></div>
                    <div><dt>Automatic sync</dt><dd className="xero-metrics__label">{needsReconnect ? 'Needs access' : config.syncMode === 'disabled' ? 'Not enabled' : rateLimited ? 'Resumes automatically' : 'On'}</dd></div>
                  </dl>
                  {needsReconnect ? (
                    <div className="xero-actions"><button type="button" className="btn btn--primary" onClick={handleConnect} disabled={Boolean(busyAction)}>{busyAction === 'connect' ? 'Reconnecting…' : 'Reconnect Xero'}</button></div>
                  ) : needsSetup ? (
                    <div className="xero-setup">
                      <p>Choose the Xero organisation for this club, then enable syncing.</p>
                      {organisationPicker}
                      <button type="button" className="btn btn--primary" onClick={handleSave} disabled={Boolean(busyAction) || !form.tenantId}>{busyAction === 'save' ? 'Saving…' : 'Enable sync'}</button>
                    </div>
                  ) : (
                    <div className="xero-actions"><button type="button" className="btn btn--primary" onClick={handleSyncNow} disabled={Boolean(busyAction) || organisationChanged}>{busyAction === 'sync_now' ? 'Syncing…' : 'Sync now'}</button><span className="text-muted">Start a pass now. Larger queues continue in the background.</span></div>
                  )}
                  {organisationChanged && !needsSetup ? <p className="text-muted">Save your organisation selection in More options before syncing.</p> : null}
                  <p className="xero-schedule text-muted">Once enabled, queued sync resumes automatically every five minutes. Daily connection and payout checks run from 03:05 UK time. If Xero limits requests, queued records wait and resume automatically.</p>
                  <dl className="xero-timestamps">
                    <div><dt>Last successful export</dt><dd>{lastSyncAt ? lastSyncAt.toLocaleString() : 'No records exported yet'}</dd></div>
                    <div><dt>Last connection check</dt><dd>{lastConnectionCheckAt ? lastConnectionCheckAt.toLocaleString() : 'Not checked yet'}</dd></div>
                  </dl>
                </>
              )}
              {healthWarning ? <p className={rateLimited || needsSetup ? 'xero-feedback' : 'text-error'} role={rateLimited || needsSetup ? 'status' : 'alert'}>{healthWarning}</p> : null}
              {config?.lastConnectionError || config?.lastSyncError ? (
                <details className="xero-error-details"><summary>Error details</summary>
                  {config.lastConnectionError ? <p className="text-error">{config.lastConnectionError}</p> : null}
                  {config.lastSyncError ? <p className="text-error">Latest sync error{latestErrorAt ? ` (${latestErrorAt.toLocaleString()})` : ''}: {config.lastSyncError}</p> : null}
                </details>
              ) : null}
            </section>

            {config?.connected ? (
                <details className="xero-panel xero-options">
                  <summary>More options</summary>
                  <div className="xero-options__content">
                    {!needsReconnect ? <div className="xero-option"><div><h4>Reconnect Xero</h4><p>Sign in again whenever you need to renew access. Authorise the same organisation to keep this connection.</p></div><button type="button" className="btn btn--secondary" onClick={handleConnect} disabled={Boolean(busyAction)}>{busyAction === 'connect' ? 'Reconnecting…' : 'Reconnect Xero'}</button></div> : null}
                    <div className="xero-option"><div><h4>Refresh account details</h4><p>Fetch the latest account names and settings from Xero. Use Sync now to process payments.</p></div><button type="button" className="btn btn--secondary" onClick={handleRefreshMetadata} disabled={Boolean(busyAction) || needsReconnect || needsSetup || organisationChanged}>{busyAction === 'refresh' ? 'Refreshing…' : 'Refresh Xero data'}</button></div>
                    {!needsSetup ? <div className="xero-option xero-option--settings"><div><h4>Connection settings</h4><p>Use the organisation that holds this club’s accounts.</p>{organisationPicker}</div><button type="button" className="btn btn--secondary" onClick={handleSave} disabled={Boolean(busyAction) || needsReconnect || !form.tenantId}>{busyAction === 'save' ? 'Saving…' : 'Save settings'}</button></div> : null}
                    <div className="xero-account"><strong>Managed Stripe clearing account</strong><p>{managedClearingAccount ? accountLabel(managedClearingAccount) : 'Created automatically when you enable syncing.'}</p></div>
                    <div className="xero-option"><div><h4>Disconnect Xero</h4><p>Stop future syncing and remove Member Nest’s access. Accounts and transactions already in Xero stay there; you can reconnect later.</p></div><button type="button" className="btn btn--danger" onClick={handleDisconnect} disabled={Boolean(busyAction)}>{busyAction === 'disconnect' ? 'Disconnecting…' : 'Disconnect'}</button></div>
                  </div>
                </details>
            ) : null}

            <section className="xero-panel" aria-labelledby="xero-how-it-works">
              <h3 id="xero-how-it-works">What appears in Xero</h3>
              <ol className="xero-explainer">
                <li><strong>A Stripe clearing account</strong><p>This represents money held in Stripe, separate from the club’s real bank account.</p></li>
                <li><strong>Payments and refunds</strong><p>Stripe payments for memberships, bolt-ons and events appear as receipts using the net amount credited to the club after fees. Descriptions include the member, purpose, period and fee breakdown where available. Refunds are recorded as adjustments.</p></li>
                <li><strong>Payouts leaving Stripe</strong><p>Payouts are recorded as money spent from the clearing account, so its balance follows the money leaving Stripe.</p></li>
              </ol>
              <div className="xero-reconciliation"><strong>Syncing and bank reconciliation are separate</strong><p>Use your bank feed in Xero to check actual bank deposits against Stripe payouts. This connection records Stripe activity; it does not connect your bank account or automatically reconcile its statement.</p></div>
            </section>

            {config?.connected ? (
              <>
                <section className="xero-panel" aria-labelledby="xero-history">
                  <h3 id="xero-history">Bring across older payments</h3>
                  <p className="text-muted">Import history adds missing past Member Nest payments and Stripe payouts to the queue. Existing exports are checked before new Xero entries are created.</p>
                  {historyState ? <p className="xero-history-status"><strong>{historyState}</strong>{backfillStatus.lastImportedTransactions != null ? ` · ${backfillStatus.lastImportedTransactions} payments` : ''}{backfillStatus.lastImportedPayouts != null ? ` · ${backfillStatus.lastImportedPayouts} payouts` : ''}</p> : null}
                  {backfillStatus?.state === 'failed' && backfillStatus.message ? <p className="text-error" role="alert">{backfillStatus.message}</p> : null}
                  <div className="xero-actions"><button type="button" className="btn btn--secondary" onClick={handleBackfill} disabled={Boolean(busyAction) || needsReconnect || needsSetup || organisationChanged || backfillStatus?.state === 'running'}>{busyAction === 'backfill' ? 'Importing history…' : 'Import history'}</button><span className="text-muted">{needsSetup ? 'Finish setup above before importing history.' : 'Queued records continue automatically. Use Sync now to start sooner.'}</span></div>
                </section>


              </>
            ) : null}
            <div className="xero-footer"><button type="button" className="btn btn--secondary" onClick={onClose}>Close</button></div>
          </div>
        )}
      </div>
    </div>, document.body
  );
}
